import React, { useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import Divider from '@material-ui/core/Divider'
import LinearProgress from '@material-ui/core/LinearProgress'
import { HeaderSection } from './header-section'
import { LocationSection } from './location-section'
import { HowSection } from './how-section'
import { RegistrationCard } from './registration-card'
import { RegistrationContainer } from 'components/program-marketing/registration-card'
import EventNotFound from 'components/program-marketing/event-not-found'
import Dialog from 'components/program-marketing/dialog'
import Container from 'components/program-marketing/container'
import { ContactCoachForm } from 'components/program-marketing/contact-coach'
import { getEventDates } from 'utils/marketingPages'
import { urls } from 'pages/junior-league-marketing/layout/menus'
import LeagueStandingSection from './league-standing-section'
import { useGetScheduleLabel } from 'lib/family-cup-utils/event-dates'
import { OnlineShopSection } from './online-shop-section/online-shop-section'
import { flags, useFeatureFlags } from 'lib/feature-flags'

export const CONTACT_COACH = gql`
  mutation ContactCoachJRL($input: JRLContactCoachInput!) {
    contactCoachJRL(input: $input) {
      message
      success
    }
  }
`

export const GET_JRL_PROGRAM = gql`
  query jrlProgram($programId: ID!) {
    jrlProgram: jrlLocalLeagueProgram(programId: $programId) {
      anticipatedEnd
      anticipatedStart
      coachingFeeInCents
      collectOnline
      description
      division
      id
      events {
        id
        date
        startTime
        endTime
        eventType
      }
      maxPlayers
      membershipFeeInCents
      name
      processingDate
      programLocationId
      published
      registrationClose
      registrationOpen
      registrationStatus
      registrationOpenings
      requirements
      seasonTbd
      status
      teams {
        id
        name
        league {
          id
          name
        }
      }
      minAge
      maxAge
      programLocation {
        id
        name
        description
        coach {
          id
          name
          phone
          profilePhoto
        }
        facility {
          name
          address1
          address2
          city
          state
          zip
          latitude
          longitude
          phone
        }
        links {
          facebook
          instagram
        }
        programOnlineShop {
          url
        }
      }
    }
  }
`

const SectionDivider = () => (
  <Container style={{ paddingTop: 0, paddingBottom: 0 }}>
    <Divider />
  </Container>
)

const NotFound = () => <EventNotFound searchUrl={urls.search} />

const EventDetails = () => {
  const { programId } = useParams()
  const location = useLocation()
  const [isXactShopUpsertEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_XACT_ENABLE_SHOP_UPSERT,
  ])
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location,
  ])

  const { data, loading, error } = useQuery(GET_JRL_PROGRAM, {
    variables: { programId },
  })

  const scheduleLabel = useGetScheduleLabel(data?.jrlProgram)

  const [activeDialog, setActiveDialog] = useState(
    searchParams.has('contactCoach') ? 'contactCoach' : null,
  )

  const viewProgramInformation = () => setActiveDialog('programInformation')
  const viewAccessCodeRequired = () => setActiveDialog('accessCode')
  const viewTeamEligibility = () => setActiveDialog('teamEligibility')
  const viewContactCoach = () => setActiveDialog('contactCoach')
  const viewAnnualMembership = () => setActiveDialog('annualMembership')
  const viewRegistrationType = () => setActiveDialog('registrationType')

  const closeActiveDialog = () => {
    setActiveDialog(null)
  }

  if (loading) return <LinearProgress color="secondary" />
  if (error || !data?.jrlProgram) return <NotFound />

  const { jrlProgram } = data

  const { programLocation, requirements, teams } = jrlProgram

  const teamsInLeague = teams.filter(team => team.league?.id)

  if (jrlProgram.status !== 'PUBLISHED') return <NotFound />

  const eventDates = getEventDates(jrlProgram.events)

  const programOnlineShopUrl = programLocation?.programOnlineShop?.url

  return (
    <>
      <HeaderSection program={jrlProgram} scheduleLabel={scheduleLabel} />

      <RegistrationContainer started={eventDates.started}>
        <RegistrationCard
          program={jrlProgram}
          scheduleLabel={scheduleLabel}
          viewAccessCodeRequired={viewAccessCodeRequired}
          viewTeamEligibility={viewTeamEligibility}
          viewAnnualMembership={viewAnnualMembership}
          viewRegistrationType={viewRegistrationType}
        />
      </RegistrationContainer>

      <LocationSection
        description={jrlProgram.description}
        location={programLocation}
        viewProgramInformation={viewProgramInformation}
        viewContactCoach={viewContactCoach}
        requirements={requirements}
      />
      {teamsInLeague.length > 0 && (
        <LeagueStandingSection teams={teamsInLeague} />
      )}
      {isXactShopUpsertEnabled && programOnlineShopUrl && (
        <>
          <SectionDivider />
          <OnlineShopSection programOnlineShopUrl={programOnlineShopUrl} />
        </>
      )}
      <SectionDivider />
      <HowSection />
      <Dialog
        heading={jrlProgram.name}
        open={activeDialog === 'programInformation'}
        onClose={closeActiveDialog}
        text={jrlProgram.description}
      />
      <Dialog
        heading="Access Code Required"
        open={activeDialog === 'accessCode'}
        onClose={closeActiveDialog}
        text="This registration requires an Access Code provided by the Host Professional. Return to our search page to find open registration programs."
      />
      <Dialog
        heading="Team Eligibility &amp; Requirements"
        open={activeDialog === 'teamEligibility'}
        onClose={closeActiveDialog}
      >
        <ul>
          <li>13u Division: Designed for players 13 and under.</li>
          <li>
            17u Division: Designed for players 14-17 years old. Players cannot
            be older than 17 years of age.
          </li>
        </ul>
        <p>
          Note: Please inquire with your PGA Coach regarding their Program's
          specific age parameters.
        </p>
        <p>
          More information about player eligibility and All-Star eligibility can
          be found{' '}
          <a href={urls.how} target="_blank" rel="noopener noreferrer">
            HERE
          </a>
        </p>
      </Dialog>
      <Dialog
        heading="Contact Coach"
        open={activeDialog === 'contactCoach'}
        onClose={closeActiveDialog}
      >
        <ContactCoachForm
          coachId={programLocation.coach.id}
          onCancel={closeActiveDialog}
          mutation={CONTACT_COACH}
          resultObjectName="contactCoachJRL"
        />
      </Dialog>
      <Dialog
        heading="Annual Membership Cost"
        open={activeDialog === 'annualMembership'}
        onClose={closeActiveDialog}
        text="PGA Jr. League players are required to purchase an annual one-time  #GameChanger Club membership for $99."
      />
      <Dialog
        heading="Registration Type"
        open={activeDialog === 'registrationType'}
        onClose={closeActiveDialog}
      >
        <ul>
          <li>
            <strong>Club Membership Required</strong>: This registration
            requires a Club Membership at the Facility and an Access Code
            provided by the PGA Coach.
          </li>
          <li>
            <strong>Access Code Required</strong>: This registration is open to
            the Public with restrictions and requires an Access Code provided by
            the PGA Coach. Contact the PGA Coach for more information.
          </li>
          <li>
            <strong>Open to Public</strong>: This registration is open to the
            public.
          </li>
        </ul>
      </Dialog>
    </>
  )
}

export default EventDetails
